import { logger } from '../utils/logger';
import type {
  ContentRecommendation,
  DashboardInsights,
  InteractionPattern,
  ContentPrioritization,
  UserPreferences,
} from '../store/slices/dashboardPersonalizationSlice';

export interface PrioritizedContent {
  id: string;
  originalPriority: number;
  adjustedPriority: number;
  reasons: string[];
  boosts: Array<{ reason: string; multiplier: number }>;
  penalties: Array<{ reason: string; multiplier: number }>;
  finalScore: number;
}

export interface PrioritizationContext {
  userPreferences?: UserPreferences;
  interactionHistory: InteractionPattern[];
  dashboardInsights?: DashboardInsights;
  timeOfDay: number;
  dayOfWeek: number;
  userSkillLevel: string;
  journeyStage: string;
  recentActivity: string[];
}

/**
 * Main content prioritization engine
 */
export class ContentPrioritizationEngine {
  private config: ContentPrioritization;

  constructor(config: ContentPrioritization) {
    this.config = config;
  }

  /**
   * Prioritize content based on multiple factors
   */
  prioritizeContent(
    content: ContentRecommendation[],
    context: PrioritizationContext
  ): PrioritizedContent[] {
    return content
      .map(item => this.calculatePriority(item, context))
      .sort((a, b) => b.finalScore - a.finalScore);
  }

  /**
   * Calculate priority for a single content item
   */
  private calculatePriority(
    content: ContentRecommendation,
    context: PrioritizationContext
  ): PrioritizedContent {
    const reasons: string[] = [];
    const boosts: Array<{ reason: string; multiplier: number }> = [];
    const penalties: Array<{ reason: string; multiplier: number }> = [];

    let score = content.priority || 0.5;
    const originalPriority = score;

    // Apply algorithm-specific scoring
    switch (this.config.algorithm) {
      case 'engagement_based':
        score = this.applyEngagementScoring(content, context, score, reasons);
        break;
      case 'ai_score':
        score = this.applyAIScoring(content, context, score, reasons);
        break;
      case 'recency':
        score = this.applyRecencyScoring(content, context, score, reasons);
        break;
      case 'hybrid':
      default:
        score = this.applyHybridScoring(content, context, score, reasons);
        break;
    }

    // Apply boosts
    for (const boost of this.config.boosts) {
      if (this.evaluateCondition(boost.condition, content, context)) {
        score *= boost.multiplier;
        boosts.push({ reason: boost.reason, multiplier: boost.multiplier });
        reasons.push(`Boosted: ${boost.reason}`);
      }
    }

    // Apply penalties
    for (const penalty of this.config.penalties) {
      if (this.evaluateCondition(penalty.condition, content, context)) {
        score *= penalty.multiplier;
        penalties.push({
          reason: penalty.reason,
          multiplier: penalty.multiplier,
        });
        reasons.push(`Penalized: ${penalty.reason}`);
      }
    }

    // Apply contextual adjustments
    score = this.applyContextualAdjustments(content, context, score, reasons);

    return {
      id: content.id,
      originalPriority,
      adjustedPriority: score,
      reasons,
      boosts,
      penalties,
      finalScore: Math.max(0, Math.min(1, score)), // Clamp between 0 and 1
    };
  }

  /**
   * Apply engagement-based scoring
   */
  private applyEngagementScoring(
    content: ContentRecommendation,
    context: PrioritizationContext,
    baseScore: number,
    reasons: string[]
  ): number {
    const contentTypeEngagement =
      context.interactionHistory
        .filter(
          h => h.sectionId === 'recommendations' && h.actionType === 'accept'
        )
        .find(h => h.actionType.includes(content.contentType))
        ?.engagementScore || 0.5;

    const engagementWeight = this.config.weights.userEngagement;
    const adjustedScore =
      baseScore * (1 - engagementWeight) +
      contentTypeEngagement * engagementWeight;

    reasons.push(
      `Engagement-based scoring: ${contentTypeEngagement.toFixed(2)}`
    );
    return adjustedScore;
  }

  /**
   * Apply AI confidence scoring
   */
  private applyAIScoring(
    content: ContentRecommendation,
    context: PrioritizationContext,
    baseScore: number,
    reasons: string[]
  ): number {
    const aiConfidence = content.explanation?.confidence || 0.5;
    const aiWeight = this.config.weights.aiConfidence;
    const adjustedScore = baseScore * (1 - aiWeight) + aiConfidence * aiWeight;

    reasons.push(`AI confidence scoring: ${aiConfidence.toFixed(2)}`);
    return adjustedScore;
  }

  /**
   * Apply recency-based scoring
   */
  private applyRecencyScoring(
    content: ContentRecommendation,
    context: PrioritizationContext,
    baseScore: number,
    reasons: string[]
  ): number {
    const now = new Date();
    const contentDate = new Date(content.createdAt || now);
    const daysSinceCreation =
      (now.getTime() - contentDate.getTime()) / (1000 * 60 * 60 * 24);

    // Newer content gets higher score (exponential decay)
    const recencyScore = Math.exp(-daysSinceCreation / 7); // 7-day half-life
    const recencyWeight = this.config.weights.recency;
    const adjustedScore =
      baseScore * (1 - recencyWeight) + recencyScore * recencyWeight;

    reasons.push(
      `Recency scoring: ${recencyScore.toFixed(2)} (${daysSinceCreation.toFixed(1)} days old)`
    );
    return adjustedScore;
  }

  /**
   * Apply hybrid scoring combining multiple factors
   */
  private applyHybridScoring(
    content: ContentRecommendation,
    context: PrioritizationContext,
    baseScore: number,
    reasons: string[]
  ): number {
    const weights = this.config.weights;
    let score = 0;

    // AI Confidence component
    const aiConfidence = content.explanation?.confidence || 0.5;
    score += aiConfidence * weights.aiConfidence;
    reasons.push(
      `AI confidence: ${aiConfidence.toFixed(2)} (weight: ${weights.aiConfidence})`
    );

    // User Engagement component
    const contentTypeEngagement = this.getContentTypeEngagement(
      content.contentType,
      context
    );
    score += contentTypeEngagement * weights.userEngagement;
    reasons.push(
      `User engagement: ${contentTypeEngagement.toFixed(2)} (weight: ${weights.userEngagement})`
    );

    // Recency component
    const recencyScore = this.getRecencyScore(content);
    score += recencyScore * weights.recency;
    reasons.push(
      `Recency: ${recencyScore.toFixed(2)} (weight: ${weights.recency})`
    );

    // Personal Relevance component
    const personalRelevance = this.getPersonalRelevance(content, context);
    score += personalRelevance * weights.personalRelevance;
    reasons.push(
      `Personal relevance: ${personalRelevance.toFixed(2)} (weight: ${weights.personalRelevance})`
    );

    // Social Proof component
    const socialProof = this.getSocialProof(content);
    score += socialProof * weights.socialProof;
    reasons.push(
      `Social proof: ${socialProof.toFixed(2)} (weight: ${weights.socialProof})`
    );

    return score;
  }

  /**
   * Get engagement score for content type
   */
  private getContentTypeEngagement(
    contentType: string,
    context: PrioritizationContext
  ): number {
    const relevantInteractions = context.interactionHistory.filter(
      h =>
        h.sectionId === 'recommendations' && h.actionType.includes(contentType)
    );

    if (relevantInteractions.length === 0) return 0.5; // Default neutral score

    const avgEngagement =
      relevantInteractions.reduce((sum, h) => sum + h.engagementScore, 0) /
      relevantInteractions.length;
    return avgEngagement;
  }

  /**
   * Get recency score
   */
  private getRecencyScore(content: ContentRecommendation): number {
    const now = new Date();
    const contentDate = new Date(content.createdAt || now);
    const daysSinceCreation =
      (now.getTime() - contentDate.getTime()) / (1000 * 60 * 60 * 24);

    return Math.exp(-daysSinceCreation / 7); // 7-day half-life
  }

  /**
   * Get personal relevance score
   */
  private getPersonalRelevance(
    content: ContentRecommendation,
    context: PrioritizationContext
  ): number {
    let relevanceScore = 0.5; // Base score

    // Check skill level alignment
    if (content.metadata?.skillLevel) {
      const skillMatch = content.metadata.skillLevel === context.userSkillLevel;
      relevanceScore += skillMatch ? 0.3 : -0.1;
    }

    // Check journey stage alignment
    if (content.metadata?.journeyStage) {
      const stageMatch = content.metadata.journeyStage === context.journeyStage;
      relevanceScore += stageMatch ? 0.2 : -0.05;
    }

    // Check recent activity alignment
    if (content.tags) {
      const tagMatches = content.tags.filter(tag =>
        context.recentActivity.includes(tag)
      ).length;
      relevanceScore += tagMatches * 0.1;
    }

    return Math.max(0, Math.min(1, relevanceScore));
  }

  /**
   * Get social proof score
   */
  private getSocialProof(content: ContentRecommendation): number {
    const rating = content.metadata?.rating || 0;
    const reviewCount = content.metadata?.reviewCount || 0;
    const popularity = content.metadata?.popularity || 0;

    // Combine rating, review count, and popularity
    const ratingScore = rating / 5; // Normalize to 0-1
    const reviewScore = Math.min(1, reviewCount / 100); // Cap at 100 reviews
    const popularityScore = Math.min(1, popularity / 1000); // Cap at 1000 views

    return ratingScore * 0.4 + reviewScore * 0.3 + popularityScore * 0.3;
  }

  /**
   * Apply contextual adjustments based on time, user state, etc.
   */
  private applyContextualAdjustments(
    content: ContentRecommendation,
    context: PrioritizationContext,
    score: number,
    reasons: string[]
  ): number {
    let adjustedScore = score;

    // Time-based adjustments
    if (content.metadata?.optimalTimeOfDay) {
      const timeDiff = Math.abs(
        context.timeOfDay - content.metadata.optimalTimeOfDay
      );
      if (timeDiff <= 2) {
        // Within 2 hours
        adjustedScore *= 1.1;
        reasons.push('Optimal time of day boost');
      } else if (timeDiff >= 6) {
        // More than 6 hours off
        adjustedScore *= 0.9;
        reasons.push('Suboptimal time of day penalty');
      }
    }

    // Day of week adjustments
    if (content.metadata?.preferredDays) {
      const dayMatch = content.metadata.preferredDays.includes(
        context.dayOfWeek
      );
      if (dayMatch) {
        adjustedScore *= 1.05;
        reasons.push('Preferred day boost');
      }
    }

    // User state adjustments
    if (context.dashboardInsights) {
      const streakDays = context.dashboardInsights.streakInfo?.current || 0;

      // Boost motivational content for users with low streaks
      if (streakDays < 3 && content.tags?.includes('motivational')) {
        adjustedScore *= 1.2;
        reasons.push('Low streak motivational boost');
      }

      // Boost challenging content for users with high streaks
      if (streakDays > 7 && content.tags?.includes('challenging')) {
        adjustedScore *= 1.15;
        reasons.push('High streak challenge boost');
      }
    }

    return adjustedScore;
  }

  /**
   * Evaluate a condition string against content and context
   */
  private evaluateCondition(
    condition: string,
    content: ContentRecommendation,
    context: PrioritizationContext
  ): boolean {
    try {
      // Simple condition evaluation - in production, use a proper expression evaluator
      if (condition.includes('contentType')) {
        const match = condition.match(/contentType\s*===?\s*['"]([^'"]+)['"]/);
        return match ? content.contentType === match[1] : false;
      }

      if (condition.includes('skillLevel')) {
        const match = condition.match(/skillLevel\s*===?\s*['"]([^'"]+)['"]/);
        return match ? context.userSkillLevel === match[1] : false;
      }

      if (condition.includes('journeyStage')) {
        const match = condition.match(/journeyStage\s*===?\s*['"]([^'"]+)['"]/);
        return match ? context.journeyStage === match[1] : false;
      }

      if (condition.includes('timeOfDay')) {
        const match = condition.match(/timeOfDay\s*([<>=]+)\s*(\d+)/);
        if (match) {
          const operator = match[1];
          const value = parseInt(match[2]);
          switch (operator) {
            case '<':
              return context.timeOfDay < value;
            case '>':
              return context.timeOfDay > value;
            case '<=':
              return context.timeOfDay <= value;
            case '>=':
              return context.timeOfDay >= value;
            case '==':
              return context.timeOfDay === value;
            default:
              return false;
          }
        }
      }

      return false;
    } catch (error) {
      logger.warn('Error evaluating condition:', condition, error);
      return false;
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ContentPrioritization>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

/**
 * Factory function to create prioritization engine with default config
 */
export function createContentPrioritizationEngine(
  customConfig?: Partial<ContentPrioritization>
): ContentPrioritizationEngine {
  const defaultConfig: ContentPrioritization = {
    algorithm: 'hybrid',
    weights: {
      aiConfidence: 0.3,
      userEngagement: 0.25,
      recency: 0.15,
      personalRelevance: 0.2,
      socialProof: 0.1,
    },
    boosts: [
      {
        condition: 'skillLevel === "beginner"',
        multiplier: 1.2,
        reason: 'Beginner-friendly content boost',
      },
      {
        condition: 'contentType === "tutorial"',
        multiplier: 1.1,
        reason: 'Tutorial content boost',
      },
      {
        condition: 'timeOfDay >= 6 && timeOfDay <= 10',
        multiplier: 1.15,
        reason: 'Morning learning boost',
      },
    ],
    penalties: [
      {
        condition: 'contentType === "advanced" && skillLevel === "beginner"',
        multiplier: 0.7,
        reason: 'Advanced content penalty for beginners',
      },
      {
        condition: 'timeOfDay >= 22 || timeOfDay <= 6',
        multiplier: 0.9,
        reason: 'Late night/early morning penalty',
      },
    ],
  };

  const config = { ...defaultConfig, ...customConfig };
  return new ContentPrioritizationEngine(config);
}

/**
 * Utility function to get prioritization context from app state
 */
export function createPrioritizationContext(
  userPreferences?: UserPreferences,
  interactionHistory: InteractionPattern[] = [],
  dashboardInsights?: DashboardInsights,
  userSkillLevel: string = 'beginner',
  journeyStage: string = 'onboarding',
  recentActivity: string[] = []
): PrioritizationContext {
  const now = new Date();

  return {
    userPreferences,
    interactionHistory,
    dashboardInsights,
    timeOfDay: now.getHours(),
    dayOfWeek: now.getDay(),
    userSkillLevel,
    journeyStage,
    recentActivity,
  };
}
